import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setMenuOpen(false);
    navigate(`/category/all?q=${encodeURIComponent(value)}`);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 border-b border-background-200 backdrop-blur" : "bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="131吃瓜 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-sm font-bold text-background-50">
            131
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg text-foreground-950 md:text-xl">{siteInfo.name}</span>
            <span className="text-[10px] tracking-[0.18em] text-foreground-500">CHI&nbsp;GUA&nbsp;CLUB</span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex" aria-label="主导航">
          {navLinks.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden items-center md:flex" role="search">
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5">
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜梗图、表情包"
              aria-label="搜索内容"
              className="w-28 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none lg:w-40"
            />
          </div>
        </form>

        <Link
          to="/contact"
          className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600 md:block"
        >
          找我们聊聊
        </Link>

        <button
          type="button"
          aria-label="打开菜单"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-2-line text-xl"}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <form onSubmit={handleSearch} className="mb-4 flex items-center gap-2" role="search">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜梗图、表情包"
                aria-label="搜索内容"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col" aria-label="移动端导航">
            {navLinks.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-sm ${
                    isActive ? "bg-primary-100 text-primary-700" : "text-foreground-800"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="mt-3 rounded-md bg-primary-500 px-3 py-3 text-center text-sm text-background-50"
            >
              联系与合作
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}